import { useEffect, useMemo, useState } from "react";
import { canViewHref, normGroups } from "@/lib/acl";
import { useCurrentUser, userHasGroup, userIsMonteure, type CurrentUser } from "@/lib/auth";
import type { NavItem } from "@/lib/nav";
import { userCanManageMitarbeiter } from "@/lib/zeiterfassungPermissions";

function hasElevatedPersonalAccess(groups: string[]): boolean {
  const normalized = normGroups(groups);
  return normalized.some(
    (g) =>
      g.includes("buero") ||
      g.includes("büro") ||
      g.includes("app-admin") ||
      g.includes("app-tester") ||
      g.includes("abrechnung") ||
      g.includes("geschaeftsfuehrung") ||
      g.includes("geschäftsführung"),
  );
}

/** Monteure / Nachunternehmer ohne Büro/Admin: Zeiterfassung nur über „Mein Bereich“. */
function isSelfServiceOnly(user: CurrentUser | null, groups: string[]): boolean {
  const isNu = userHasGroup(user, "Nachunternehmer");
  if (!userIsMonteure(user) && !isNu) return false;
  return !hasElevatedPersonalAccess(groups);
}

export interface PersonalHubCard {
  key: string;
  title: string;
  description: string;
  href: string;
  internal?: boolean;
  aclHref?: string;
}

export const PERSONAL_CHILD_ITEMS: NavItem[] = [
  {
    key: "mein-bereich",
    label: "Mein Bereich",
    href: "/personal/me",
    internal: true,
    aclHref: "/personal/me.html",
  },
  {
    key: "zeiterfassung",
    label: "Zeiterfassung",
    href: "/zeiterfassung",
    internal: true,
    aclHref: "/zeiterfassung.html",
  },
  {
    key: "mitarbeiter",
    label: "Mitarbeiterverwaltung",
    href: "/admin/mitarbeiter",
    internal: true,
    aclHref: "/admin/mitarbeiter.html",
  },
  {
    key: "personalkosten",
    label: "Personalkosten",
    href: "/personal/personalkosten",
    internal: true,
    aclHref: "/personal/personalkosten.html",
  },
  {
    key: "nachunternehmer",
    label: "Nachunternehmer",
    href: "/personal/nachunternehmer",
    internal: true,
    aclHref: "/nachunternehmer.html",
  },
];

export const PERSONAL_HUB_NAV: NavItem = {
  key: "personal",
  label: "Personal",
  href: "/personal",
  internal: true,
  aclHref: "/personal.html",
  activePrefixes: [
    "/personal",
    "/personal/me",
    "/zeiterfassung",
    "/admin/mitarbeiter",
    "/admin/zeiterfassung",
    "/personal/personalkosten",
    "/personal/nachunternehmer",
    "/alt/nachunternehmer",
    "/nachunternehmer",
  ],
};

export const PERSONAL_HUB_CARDS: PersonalHubCard[] = [
  {
    key: "mein-bereich",
    title: "Mein Bereich",
    description: "Eigene Arbeitszeiten erfassen, Abwesenheiten beantragen und den Jahreskalender mit Hero-Daten einsehen.",
    href: "/personal/me",
    internal: true,
    aclHref: "/personal/me.html",
  },
  {
    key: "zeiterfassung",
    title: "Zeiterfassung",
    description: "Stunden erfassen, Abwesenheiten beantragen sowie Monats- und Jahresübersicht einsehen.",
    href: "/zeiterfassung",
    internal: true,
    aclHref: "/zeiterfassung.html",
  },
  {
    key: "mitarbeiter",
    title: "Mitarbeiterverwaltung",
    description: "Mitarbeiter im Überblick, Abwesenheits-Kontingente und Überstunden-Startstände pflegen.",
    href: "/admin/mitarbeiter",
    internal: true,
    aclHref: "/admin/mitarbeiter.html",
  },
  {
    key: "personalkosten",
    title: "Personalkosten",
    description: "Laufende Kosten erfassen und voll belastete Stundenlöhne berechnen.",
    href: "/personal/personalkosten",
    internal: true,
    aclHref: "/personal/personalkosten.html",
  },
  {
    key: "nachunternehmer",
    title: "Nachunternehmer",
    description: "Zeiten prüfen, Rechnungen zuordnen und als bezahlt markieren.",
    href: "/personal/nachunternehmer",
    internal: true,
    aclHref: "/nachunternehmer.html",
  },
];

async function canViewPersonalChild(
  item: Pick<NavItem, "key" | "aclHref" | "href">,
  user: CurrentUser | null,
  groups: string[],
): Promise<boolean> {
  if (item.key === "mitarbeiter") return userCanManageMitarbeiter(user);
  return canViewHref(item.aclHref ?? item.href, groups);
}

export async function resolveVisiblePersonalItems(
  user: CurrentUser | null,
  groups: string[],
): Promise<NavItem[]> {
  const selfServiceOnly = isSelfServiceOnly(user, groups);
  const candidates = selfServiceOnly
    ? PERSONAL_CHILD_ITEMS.filter((item) => item.key === "mein-bereich")
    : PERSONAL_CHILD_ITEMS;
  const checks = await Promise.all(
    candidates.map((item) => canViewPersonalChild(item, user, groups)),
  );
  return candidates.filter((_, index) => checks[index]);
}

/** Nav-Einträge: Hub bei ≥2 sichtbaren Seiten, sonst die einzelne Seite direkt. */
export async function resolvePersonalNavItems(
  user: CurrentUser | null,
  groups: string[],
): Promise<NavItem[]> {
  const visible = await resolveVisiblePersonalItems(user, groups);
  if (visible.length === 0) return [];
  if (visible.length === 1) return visible;
  return [PERSONAL_HUB_NAV];
}

export async function resolveVisiblePersonalHubCards(
  user: CurrentUser | null,
  groups: string[],
): Promise<PersonalHubCard[]> {
  const selfServiceOnly = isSelfServiceOnly(user, groups);
  const candidates = selfServiceOnly
    ? PERSONAL_HUB_CARDS.filter((card) => card.key === "mein-bereich")
    : PERSONAL_HUB_CARDS;
  const checks = await Promise.all(
    candidates.map((item) => canViewPersonalChild(item, user, groups)),
  );
  return candidates.filter((_, index) => checks[index]);
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const href = item.href;
  if (href === "/") return pathname === "/";
  if (pathname === href || pathname.startsWith(`${href}/`)) return true;
  return (item.activePrefixes ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function personalBackLink(visibleItems: NavItem[]): { to: string; label: string } | null {
  // Bei mehreren Bereichen übernimmt die linke Sidebar die Navigation.
  if (visibleItems.length >= 2) return null;
  if (visibleItems.length === 1) {
    const only = visibleItems[0];
    if (!only) return null;
    return { to: only.href, label: only.label };
  }
  return null;
}

/** Pfade, die zur Personal-Sektion gehören (für Layout-Sidebar). */
export function isPersonalSectionPath(pathname: string): boolean {
  const path = (pathname || "").replace(/\/$/, "") || "/";
  if (path === "/personal" || path.startsWith("/personal/")) return true;
  if (path === "/zeiterfassung" || path.startsWith("/zeiterfassung/")) return true;
  if (path === "/admin/mitarbeiter" || path.startsWith("/admin/mitarbeiter/")) return true;
  if (path === "/admin/zeiterfassung" || path.startsWith("/admin/zeiterfassung/")) return true;
  if (path.startsWith("/alt/nachunternehmer") || path.startsWith("/nachunternehmer")) {
    return true;
  }
  return false;
}

export function usePersonalNavigation() {
  const { user, loading: userLoading } = useCurrentUser();
  const groups = useMemo(() => normGroups((user?.groups as string[] | undefined) ?? []), [user]);
  const [visibleItems, setVisibleItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (userLoading) return;
    (async () => {
      setLoading(true);
      try {
        const items = await resolveVisiblePersonalItems(user, groups);
        if (!cancelled) setVisibleItems(items);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, groups, userLoading]);

  return {
    visibleItems,
    showHub: visibleItems.length >= 2,
    loading: userLoading || loading,
    backLink: personalBackLink(visibleItems),
  };
}
